import { supabase } from '../lib/supabase';
import { calculateMatchMinute, TimingPoint } from '../utils/videoTimingUtils';

export class VideoTagsSyncService {
  // Stat types to exclude from match_video_tags (same as match_timeline)
  private static readonly EXCLUDED_STAT_TYPES = [
    'short_pass_success',
    'short_pass_failure',
    'short_pass_backward_success',
    'short_pass_backward_failure',
    'long_pass_success',
    'long_pass_failure',
    'long_pass_backward_success',
    'long_pass_backward_failure',
    'tackle_success',
    'tackle_failure',
    'throw_in',
    'interception',
    'clearance'
  ];

  /**
   * Generate auto-computed tags from stat type (reuse from KeyEventsSyncService)
   */
  private static generateAutoTags(statKey: string, log: any): string[] {
    // Reuse the generateEventTags method from KeyEventsSyncService
    const { KeyEventsSyncService } = require('./keyEventsSyncService');
    return KeyEventsSyncService.generateEventTags(statKey, log);
  }

  /**
   * Merge auto-computed tags with manual tags
   */
  private static mergeTags(autoTags: string[], manualTags: string[] | null): string[] {
    const merged = [...autoTags];
    
    if (manualTags && Array.isArray(manualTags) && manualTags.length > 0) {
      // Add manual tags, avoiding duplicates
      manualTags.forEach(tag => {
        if (!merged.includes(tag)) {
          merged.push(tag);
        }
      });
    }
    
    return merged;
  }

  /**
   * Sync scoresheet_logs to match_video_tags
   * Filters: Same as match_timeline (exclude certain stat types) + include if manual_tags exists
   */
  static async syncVideoTagsFromScoresheetLogs(
    matchId: string,
    logs: any[],
    userId?: string
  ): Promise<{ syncedCount: number }> {
    if (logs.length === 0) {
      return { syncedCount: 0 };
    }

    // Filter logs:
    // 1. Must have video timing (video_time_seconds AND video_id)
    // 2. Either: NOT in excluded stat types OR has manual_tags
    const videoTagLogs = logs.filter(log => {
      // Must have video timing (allow video_time_seconds = 0, but not null/undefined)
      if (log.video_time_seconds == null || !log.video_id) {
        return false;
      }

      const statType = log.stat_types as any;
      const statKey = statType?.stat_key;

      // Include if:
      // - NOT in excluded stat types (same as match_timeline)
      // OR
      // - Has manual_tags (regardless of stat type)
      const hasManualTags = log.manual_tags && 
        Array.isArray(log.manual_tags) && 
        log.manual_tags.length > 0;
      
      const isExcluded = this.EXCLUDED_STAT_TYPES.includes(statKey);
      
      return !isExcluded || hasManualTags;
    });

    if (videoTagLogs.length === 0) {
      return { syncedCount: 0 };
    }

    // Filter out decrement actions - video tags should only have increments
    const incrementVideoTagLogs = videoTagLogs.filter(log => log.action === 'increment');

    if (incrementVideoTagLogs.length === 0) {
      return { syncedCount: 0 };
    }

    // Check which video tags already exist (by source_log_id) - include tags and match_minute to check for changes
    // Check by source_log_id regardless of is_active status to prevent duplicates
    const logIds = incrementVideoTagLogs.map(log => log.id);
    const { data: existingTags, error: existingError } = await supabase
      .from('match_video_tags')
      .select('id, source_log_id, tags, match_minute')
      .eq('match_id', matchId)
      .in('source_log_id', logIds);

    if (existingError) {
      throw new Error(`Failed to check existing video tags: ${existingError.message}`);
    }

    // Create a map: source_log_id -> { tag_id, tags, match_minute }
    const existingTagsMap = new Map<string, { id: string; tags: string[]; match_minute: number | null }>();
    existingTags?.forEach(tag => {
      if (tag.source_log_id) {
        existingTagsMap.set(tag.source_log_id, {
          id: tag.id,
          tags: tag.tags || [],
          match_minute: tag.match_minute
        });
      }
    });

    // Separate logs into new tags and tags that need updates
    const newTags: any[] = [];
    const tagsToUpdate: Array<{ tagId: string; log: any; mergedTags: string[]; calculatedMatchMinute: number | null }> = [];

    // Process each log to determine if it needs to be created or updated
    await Promise.all(
      incrementVideoTagLogs.map(async (log) => {
        const existingTag = existingTagsMap.get(log.id);
        
        if (!existingTag) {
          // New tag - add to insert list
          newTags.push(log);
          return;
        }

        // Tag exists - check if we need to update
        const statType = log.stat_types as any;
        const statKey = statType?.stat_key;

        // Generate auto-tags
        const autoTags = this.generateAutoTags(statKey, log);

        // Merge with manual_tags
        const mergedTags = this.mergeTags(autoTags, log.manual_tags);

        // Recalculate match_minute (in case timing_points changed)
        let calculatedMatchMinute: number | null = null;
        try {
          const { matchVideosService } = await import('./matchVideosService');
          const timingPointsResult = await matchVideosService.getTimingPoints(log.video_id);
          
          if (timingPointsResult.success && timingPointsResult.data) {
            calculatedMatchMinute = calculateMatchMinute(
              log.video_time_seconds,
              timingPointsResult.data as TimingPoint[]
            );
          }
        } catch (error) {
          console.warn(`Error calculating match minute for log ${log.id}:`, error);
        }

        // Use log's match_minute as fallback
        const finalMatchMinute = calculatedMatchMinute ?? log.match_minute ?? null;

        // Check if tags or match_minute have actually changed
        const tagsChanged = JSON.stringify(existingTag.tags.sort()) !== JSON.stringify(mergedTags.sort());
        const matchMinuteChanged = existingTag.match_minute !== finalMatchMinute;

        // Only add to update list if something actually changed
        if (tagsChanged || matchMinuteChanged) {
          tagsToUpdate.push({
            tagId: existingTag.id,
            log,
            mergedTags,
            calculatedMatchMinute: finalMatchMinute
          });
        }
      })
    );

    let syncedCount = 0;

    // Insert new tags
    if (newTags.length > 0) {
      const tagsToInsert = await Promise.all(
        newTags.map(async (log) => {
          const statType = log.stat_types as any;
          const statKey = statType?.stat_key;

          // Generate auto-tags
          const autoTags = this.generateAutoTags(statKey, log);

          // Merge with manual_tags
          const mergedTags = this.mergeTags(autoTags, log.manual_tags);

          // Calculate match_minute from video_time_seconds using timing_points
          let calculatedMatchMinute: number | null = null;
          try {
            const { matchVideosService } = await import('./matchVideosService');
            const timingPointsResult = await matchVideosService.getTimingPoints(log.video_id);
            
            if (timingPointsResult.success && timingPointsResult.data) {
              calculatedMatchMinute = calculateMatchMinute(
                log.video_time_seconds,
                timingPointsResult.data as TimingPoint[]
              );
            }
          } catch (error) {
            console.warn(`Error calculating match minute for log ${log.id}:`, error);
          }

          return {
            match_id: log.match_id,
            video_id: log.video_id,
            source_log_id: log.id,
            team_id: log.team_id || null,
            player_id: log.player_id || null,
            stat_type_id: log.stat_type_id || null,
            video_time_seconds: log.video_time_seconds,
            match_minute: calculatedMatchMinute ?? log.match_minute ?? null,
            tags: mergedTags,
            created_by: userId || log.user_id || 'system',
            updated_by: userId || log.user_id || 'system',
            is_active: log.action === 'increment' // Decrements default to inactive
          };
        })
      );

      const { error: insertError } = await supabase
        .from('match_video_tags')
        .insert(tagsToInsert);

      if (insertError) {
        throw new Error(`Failed to insert video tags: ${insertError.message}`);
      }

      syncedCount += tagsToInsert.length;
      console.log(`Inserted ${tagsToInsert.length} new video tags for match ${matchId}`);
    }

    // Update existing tags (only if tags or match_minute changed)
    if (tagsToUpdate.length > 0) {
      const updatePromises = tagsToUpdate.map(({ tagId, log, mergedTags, calculatedMatchMinute }) => {
        return supabase
          .from('match_video_tags')
          .update({
            tags: mergedTags,
            match_minute: calculatedMatchMinute,
            updated_by: userId || log.user_id || 'system',
            updated_at: new Date().toISOString()
          })
          .eq('id', tagId);
      });

      const updateResults = await Promise.all(updatePromises);
      
      // Check for errors
      const errors = updateResults.filter(result => result.error);
      if (errors.length > 0) {
        console.error(`Failed to update ${errors.length} video tags`);
        // Don't throw - log error but continue
      } else {
        syncedCount += tagsToUpdate.length;
        console.log(`Updated ${tagsToUpdate.length} existing video tags for match ${matchId}`);
      }
    }

    if (syncedCount === 0) {
      console.log('No new or updatable video tags for match:', matchId);
    }

    return { syncedCount };
  }

  /**
   * Sync a single log to match_video_tags (for real-time sync on stat save)
   */
  static async syncVideoTagsForLog(
    matchId: string,
    logId: string,
    userId?: string
  ): Promise<{ synced: boolean; tagId?: string }> {
    try {
      // Get the log with video timing
      const { data: log, error: logError } = await supabase
        .from('scoresheet_logs')
        .select(`
          id,
          match_id,
          video_id,
          video_time_seconds,
          match_minute,
          team_id,
          player_id,
          stat_type_id,
          manual_tags,
          user_id,
          action,
          stat_types!inner(stat_key, display_name, category, event_result)
        `)
        .eq('id', logId)
        .eq('is_active', true)
        .single();

      if (logError || !log) {
        throw new Error(`Failed to fetch log: ${logError?.message || 'Log not found'}`);
      }

      // Must have video timing (allow video_time_seconds = 0, but not null/undefined)
      if (log.video_time_seconds == null || !log.video_id) {
        return { synced: false };
      }

      const statType = log.stat_types as any;
      const statKey = statType?.stat_key;

      // Check if should sync (same filtering logic)
      const hasManualTags = log.manual_tags && 
        Array.isArray(log.manual_tags) && 
        log.manual_tags.length > 0;
      
      const isExcluded = this.EXCLUDED_STAT_TYPES.includes(statKey);
      
      // Skip if excluded and no manual tags
      if (isExcluded && !hasManualTags) {
        return { synced: false };
      }

      // Check if tag already exists (include tags and match_minute to check for changes)
      const { data: existingTag } = await supabase
        .from('match_video_tags')
        .select('id, tags, match_minute')
        .eq('source_log_id', logId)
        .eq('is_active', true)
        .single();

      // Generate auto-tags
      const autoTags = this.generateAutoTags(statKey, log);

      // Merge with manual_tags
      const mergedTags = this.mergeTags(autoTags, log.manual_tags);

      // Calculate match_minute from video_time_seconds using timing_points
      let calculatedMatchMinute: number | null = null;
      try {
        const { matchVideosService } = await import('./matchVideosService');
        const timingPointsResult = await matchVideosService.getTimingPoints(log.video_id);
        
        if (timingPointsResult.success && timingPointsResult.data) {
          calculatedMatchMinute = calculateMatchMinute(
            log.video_time_seconds,
            timingPointsResult.data as TimingPoint[]
          );
        }
      } catch (error) {
        console.warn(`Error calculating match minute for log ${logId}:`, error);
      }

      const finalMatchMinute = calculatedMatchMinute ?? log.match_minute ?? null;

      if (existingTag) {
        // Check if tags or match_minute have actually changed
        const existingTags = existingTag.tags || [];
        const tagsChanged = JSON.stringify(existingTags.sort()) !== JSON.stringify(mergedTags.sort());
        const matchMinuteChanged = existingTag.match_minute !== finalMatchMinute;

        // Only update if something actually changed
        if (tagsChanged || matchMinuteChanged) {
          const { error: updateError } = await supabase
            .from('match_video_tags')
            .update({
              tags: mergedTags,
              match_minute: finalMatchMinute,
              updated_by: userId || log.user_id || 'system',
              updated_at: new Date().toISOString()
            })
            .eq('id', existingTag.id);

          if (updateError) {
            throw new Error(`Failed to update video tag: ${updateError.message}`);
          }

          return { synced: true, tagId: existingTag.id };
        } else {
          // No changes - return synced: false to indicate no update was needed
          return { synced: false, tagId: existingTag.id };
        }
      } else {
        // Create new tag
        const { data: newTag, error: insertError } = await supabase
          .from('match_video_tags')
          .insert({
            match_id: log.match_id,
            video_id: log.video_id,
            source_log_id: log.id,
            team_id: log.team_id || null,
            player_id: log.player_id || null,
            stat_type_id: log.stat_type_id || null,
            video_time_seconds: log.video_time_seconds,
            match_minute: calculatedMatchMinute ?? log.match_minute ?? null,
            tags: mergedTags,
            created_by: userId || log.user_id || 'system',
            updated_by: userId || log.user_id || 'system',
            is_active: (log.action || 'increment') === 'increment' // Decrements default to inactive, undefined defaults to increment
          })
          .select('id')
          .single();

        if (insertError) {
          throw new Error(`Failed to create video tag: ${insertError.message}`);
        }

        return { synced: true, tagId: newTag?.id };
      }
    } catch (error) {
      console.error('Error syncing video tag for log:', error);
      throw error;
    }
  }
}

